import {
  Backdrop,
  Box,
  useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
//import { differenceInSeconds } from "date-fns";
import { ArrowLeft } from "lucide-react";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import {
  ButtonDigitedLink,
  StyledButtonBookmark,
  StyledCoverLoader,
  StyledPlayerLoader, StyledScormPlayButton,
  StyledStatusBoxPlayer,
  VideoStack
} from "./Video.styles";
import { VideoComponentProps } from "./Video.types";
import {
  CtaOverlay,
  GridMain,
  ScormWrapper,
  StatusBox,
  VideoCaptionOverlay
} from "../../components";
import { ButtonDigited } from "../../components/Button/Button.variants";
import {
  LEVEL,
  STATUS_LO
} from "../../consts";
import { UserNavigationContext } from "../../context/UserNavigationContext";
import { useFullscreenStatus } from "../../hooks";
import { useInvalidateStartOrResumeQuery } from "../../queries";
import { useNavigate } from "../../services";
import { useAuthStore, useNavbarStore } from "../../stores";
import { useTabManager } from "../../useTabManager";
import { getNavigationTarget, getPreviousRoute } from "../../utils/general";
import LearningActivityBlockOverlay from "../LearningActivityBlockOverlay";


const EXTERNAL_LEVEL = {
  [LEVEL.DA_SCORM]: LEVEL.DA_SCORM,
  [LEVEL.DA_XAPI]: LEVEL.DA_XAPI,
  [LEVEL.DA_SURVEY]: LEVEL.DA_SURVEY,
  [LEVEL.DA_YOUTUBE]: LEVEL.DA_YOUTUBE,
  [LEVEL.DA_EXTERNAL_RES]: LEVEL.DA_EXTERNAL_RES
} as const;
type ExternalContent = keyof typeof EXTERNAL_LEVEL

export function VideoComponent({
  autoplay,
  borderRadius,
  courseDetails,
  courseId,
  coverFallBack,
  height,
  id,
  isFavourite,
  isMandatory,
  learningObjectType,
  learningObjectTypology,
  learningObjectStarted,
  moduleNumber,
  pathId,
  rating,
  rootDetails,
  refetchParentCourse,
  refetchParentRoot,
  shortDescription,
  status,
  title,
  videoId,
  tentativeId,
  isExternal,
  width = "100%",
  canAccessNow,
  canAccessUntil,
  contentTabs,
  ...props
}: VideoComponentProps) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isLandscape = useMediaQuery("(orientation: landscape)");
  const isFullScreen = useFullscreenStatus();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { isActive } = useTabManager();

  //const [autoplaySet, setAutoPlaySet] = useState(autoplay);
  const [buttonSelected, setButtonSelected] = useState(true);
  const [externalIsOpen, setExternalIsOpen] = useState<ExternalContent | null>(null);
  const [openBackdrop, setOpenBackdrop] = useState(isExternal);
  const { navigation, setNavigation } = useContext(UserNavigationContext);
  const [showSessionTimeoutOverlay, setShowSessionTimeoutOverlay] = useState(false);
  const b2cFlag = useAuthStore(state => state.corporateInfo?.b2c_flag);
  const navbarItem = useNavbarStore(state => state.item);

  const previousRoute = getPreviousRoute({
    id,
    rootId: pathId ? Number(pathId) : NaN,
    courseId: courseId ? Number(courseId) : NaN,
    path: pathname,
    section: navbarItem,
    learningObjectTypology
  });

  const { invalidateQueryStartOrResume } = useInvalidateStartOrResumeQuery({
    grandParentId: learningObjectStarted.grandParentId ? learningObjectStarted.grandParentId.toString() : undefined,
    learningObjectId: learningObjectStarted.id.toString(),
    learningObjectTypology: learningObjectStarted.learningObjectTypology,
    parentId: learningObjectStarted.parentId ? learningObjectStarted.parentId.toString() : undefined
  });

  const onEndAction = () => {
    invalidateQueryStartOrResume();
    //setAutoPlaySet(learningObjectStarted.id !== id);
    if (rootDetails?.id) {
      refetchParentRoot();
    }
    if (courseDetails?.id) {
      refetchParentCourse();
    }
  };

  useEffect(() => {
    if (!isActive) {
      setOpenBackdrop(false);
    }
  }, [isActive]);
  

  // const isExternal =
  //   learningObjectTypology === EXTERNAL_LEVEL.DA_SCORM
  //   || learningObjectTypology === EXTERNAL_LEVEL.DA_XAPI
  //   || learningObjectTypology === EXTERNAL_LEVEL.DA_SURVEY
  //   || learningObjectTypology === EXTERNAL_LEVEL.DA_YOUTUBE;

  const isFromYoutube = learningObjectTypology === EXTERNAL_LEVEL.DA_YOUTUBE;
  const isExternalRes = learningObjectTypology === EXTERNAL_LEVEL.DA_EXTERNAL_RES;

  // const uri = `${window.location.origin}/esplora/scorm/${id}/${learningObjectTypology}`;
  // const params = `courseId=${courseId ?? null}&rootId=${parentId ?? null}`;

  // const handleSaveCourse = () => {
  //   setOpenBackdrop(false);
  // };
  useEffect(() => {
    document.body.style.overflow = (openBackdrop) ? "hidden" : "auto";
  }, [openBackdrop]);

  useEffect(() => {
    window.onpopstate = () => {
      document.body.removeAttribute("style");
    };
  }, []);

  window.addEventListener("message", (e) => {
    if (e.data == "closeCoursesModal") {
      document.body.removeAttribute("style");
      setOpenBackdrop(false);

      let rootCalculatedId: number | null = rootDetails?.learningObjectTypology === "PATH" ? rootDetails.id : null;
      let courseCalculatedId: number | null = null;

      if (rootDetails?.learningObjectTypology === "COURSE" && rootDetails.isStandAlone) {
        courseCalculatedId = rootDetails.id;
        rootCalculatedId = null;
      }

      if (!navigation?.length) return;
      const isForYou = pathname.includes("per-te");
      const isEsplora = pathname.includes("esplora");

      if (isForYou || isEsplora) {
        navigate.navigateToDetail({
          courseId: courseCalculatedId || courseDetails?.id,
          id,
          isForYou,
          learningObjectTypology,
          rootId: rootCalculatedId || undefined
        });
      }
    }
  });

  const handleOverlayClose = () => {
    setShowSessionTimeoutOverlay(false);
    setOpenBackdrop(false);
  };

  return (
    <>
      { (canAccessNow === false || showSessionTimeoutOverlay) && <LearningActivityBlockOverlay onClose={ handleOverlayClose } previousRoute={ previousRoute } /> }
      <VideoStack
        $isExternal={ isExternal }
        height={ isExternal ? height : isMobile ? "100%" : height }
        width={ width }
        { ...props }
      >
        {
          (isExternal || !Number(videoId)) ? (
            <StyledCoverLoader
              minHeight={ "750px" }
              position="relative"
              $cover={ externalIsOpen ? undefined : coverFallBack }
              $videoVisible={ Boolean(externalIsOpen) }
              sx={ {
                [`${theme.breakpoints.down("sm")}`]: {
                  minHeight: "450px"
                }
              } }
            >
              <GridMain
                margin={ "0 auto" }
                minHeight={ "750px" }
                position={ "absolute" }
                sx={ {
                  inset: 0,
                  [`${theme.breakpoints.down("sm")}`]: {
                    minHeight: "450px"
                  }
                } }
              >
                <StyledButtonBookmark
                  aria-label={ t("activities_show") }
                  disableRipple
                  $isFromYoutube={ isFromYoutube }
                  onClick={ () => { showTabs() } }
                  $selected={ buttonSelected }
                  sx={ { "&:hover": { border: "none" } } }
                >
                  <div></div>
                </StyledButtonBookmark>
                {
                  status === STATUS_LO.C ? (
                    <StyledStatusBoxPlayer>
                      <StatusBox
                        accent={ theme.customColors.accentVariantB }
                        icon={ "completato" }
                      />
                    </StyledStatusBoxPlayer>
                  ) : null
                }
                <ButtonDigited
                  aria-label={ t("back") }
                  typology="secondary"
                  buttonSize="md"
                  sx={ { 
                    gap:"9px",
                    position:"absolute",
                    zIndex: "1",
                    left:"42px",
                    top:"32px",
                    [theme.breakpoints.down("sm")]: {
                      left: "20px",
                      minWidth: "auto",
                      top: "16px"
                    }
                  } }
                  onClick={ () => {
                    const { newNavigation, target } = getNavigationTarget(navigation);
                    target ? navigate(target) : navigate(-1);
                    setNavigation && setNavigation(newNavigation);
                  } }
                  disableRipple
                >
                  <>
                    <ArrowLeft color={ theme.customColors.textPrimary } size={ 20 } />
                    { t("back") }
                  </>
                </ButtonDigited>

                {
                  !externalIsOpen ? (
                    <VideoCaptionOverlay
                      courseDetails={ courseDetails }
                      learningObjectType={ learningObjectType }
                      learningObjectTypology={ learningObjectTypology }
                      moduleNumber={ moduleNumber }
                      rootDetails={ rootDetails }
                      title={ title }
                    />
                  ) : null
                }
                <CtaOverlay
                  id={ id }
                  isFavourite={ isFavourite }
                  learningObjectTypology={ learningObjectTypology }
                  rating={ rating }
                  share={ true }
                  courseId={ Number(courseId) }
                  rootId={ Number(pathId) }
                />
                {
                  !externalIsOpen ? (
                    <Box
                      className={ "icon-play" }
                      sx={ { pointerEvents: "auto" } }
                      onClick={ () => {
                        if (isExternal) {
                          // window.open(`${uri}?${params}`, "_blank", "noreferrer");
                          setOpenBackdrop(true);
                        }
                        else if (isFromYoutube) {
                          setExternalIsOpen(learningObjectTypology);
                        }

                      } }
                    >
                      <StyledScormPlayButton>
                        <span/>
                      </StyledScormPlayButton>
                    </Box>
                  ) : null
                }

              </GridMain>
              {
                externalIsOpen && isFromYoutube ?
                  <ScormWrapper
                    activityId={ id }
                    // close={ () => setExternalIsOpen(null) }
                    courseId={ courseId }
                    // fullScreen={ externalIsOpen !== EXTERNAL_LEVEL.DA_YOUTUBE }
                    isOpen={ Boolean(externalIsOpen) }
                    learningObjectTypology={ learningObjectTypology }
                    learningObjectStarted={ learningObjectStarted }
                    pathId={ pathId }
                  /> : null
              }
            </StyledCoverLoader>
          ) : (
            <StyledPlayerLoader
              autoplay={ autoplay && !showSessionTimeoutOverlay } // autoplay={ autoplaySet && !showSessionTimeoutOverlay }
              borderRadius={ borderRadius?.toString() }
              canPlayBack={ (status !== STATUS_LO.C && isMandatory) || Boolean(b2cFlag) }
              courseId={ courseId }
              coverFallBack={ coverFallBack }
              id={ id }
              onEndAction={ onEndAction }
              parentId={ pathId }
              position="relative"
              shortDescription={ shortDescription }
              title={ title }
              triggerPause={ showSessionTimeoutOverlay }
              videoId={ videoId }
              tentativeId={ tentativeId }
              contentTabs={ contentTabs }
              canAccessNow={ canAccessNow }
              canAccessUntil={ canAccessUntil }
              setTriggerPause={ setShowSessionTimeoutOverlay }
              isMobile={ isMobile }
              isLandscape={ isLandscape }
              isFullScreen={ isFullScreen }
            >
              {
                <GridMain
                  margin={ "0 auto" }
                  height={ "100%" }
                  position={ "absolute" }
                  sx={ { inset: 0, zIndex: "3 !important" } }
                >
                  {
                    status === "C" ? (
                      <StyledStatusBoxPlayer>
                        <StatusBox
                          accent={ theme.customColors.accentVariantB }
                          icon={ "completato" }
                        />
                      </StyledStatusBoxPlayer>
                    ) : null
                  }
                  <StyledButtonBookmark
                    aria-label={ t("activities_show") }
                    disableRipple
                    $isFromYoutube={ false }
                    onClick={ () => { showTabs() } }
                    $selected={ buttonSelected }
                    sx={ { "&:hover": { border: "none" } } }
                  >
                    <div></div>
                  </StyledButtonBookmark>
                  <ButtonDigitedLink
                    aria-label={ t("back") }
                    typology="secondary"
                    buttonSize="md"
                    onClick={ () => {
                      //setTriggerPausePlayer(true);
                      if(previousRoute) {
                        navigate(previousRoute);
                      }else {
                        const { newNavigation, target } = getNavigationTarget(navigation);
                        target ? navigate(target) : navigate(-1);
                        setNavigation && setNavigation(newNavigation);
                      }
                    } }
                    disableRipple
                  >
                    <>
                      <ArrowLeft
                        style={ {
                          position:"relative",
                          right: "0.25rem"
                        } } 
                        color={ theme.customColors.textPrimary } 
                        size="1.25rem" />
                      { t("back") }
                    </>
                  </ButtonDigitedLink>
                  <VideoCaptionOverlay
                    courseDetails={ courseDetails }
                    learningObjectType={ learningObjectType }
                    learningObjectTypology={ learningObjectTypology }
                    rootDetails={ rootDetails }
                    title={ title }
                  />
                  <CtaOverlay
                    id={ id }
                    isFavourite={ isFavourite }
                    learningObjectTypology={ learningObjectTypology }
                    rating={ rating }
                    share={ true }
                    courseId={ Number(courseId) }
                    rootId={ Number(pathId) }
                  />
                </GridMain>
              }
            </StyledPlayerLoader>
          )
        }
      </VideoStack>
      { isExternalRes ?
        <></> :
        openBackdrop &&
          <Backdrop
            sx={ { background: "rgba(255,255,255,0.3)", padding: 4, zIndex: 600000 } }
            open={ openBackdrop }
          >
            { /* <button onClick={ () => backNavigation() }>Indietro</button> */ }
            <Box sx={ {
              background: "#000",
              height: "100%",
              position: "relative",
              width: "100%"
            } }>
              <ScormWrapper
                activityId={ id }
                close={ () => setOpenBackdrop(false) }
                courseId={ courseId }
                // fullScreen={ externalIsOpen !== EXTERNAL_LEVEL.DA_YOUTUBE }
                isOpen={ Boolean(openBackdrop) }
                learningObjectTypology={ learningObjectTypology }
                learningObjectStarted={ learningObjectStarted }
                pathId={ pathId }
                canAccessNow={ canAccessNow }
                canAccessUntil={ canAccessUntil }
                setTriggerPause={ setShowSessionTimeoutOverlay }
              />
            </Box>
          </Backdrop>
      }
    </>
  );

  function showTabs() {
    const $tabsComponent = document.querySelector(".tab-component") as HTMLDivElement;
    if ($tabsComponent) {
      if (buttonSelected) {
        setButtonSelected(false);
        $tabsComponent.classList.remove("is-visible");
      } else {
        setButtonSelected(true);
        $tabsComponent.classList.add("is-visible");
      }
    }
  }
}
